import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Copy, Pencil, ArrowUp, ArrowDown, GripVertical, X, Upload, History, Palette, Ban, RotateCcw, FolderInput } from 'lucide-react'
import { api } from '../../utils/api'
import { Status, StatusSet, Frequency, Kind, FREQ_LABEL, GROUP_KIND_LABEL, KIND_LABEL, errMsg } from './shared'

interface Item {
  id: string; groupId: string; parentId: string | null; isHost: boolean; checkable: boolean; name: string; statusSetId: string | null
  sortOrder: number; activeFrom: string | null; activeTo: string | null; recordCount: number
}
interface Group {
  id: string; name: string; frequency: Frequency; kind: string; statusSetId: string; isActive: boolean; sortOrder: number; items: Item[]
}
interface Config { today: string; statusSets: StatusSet[]; groups: Group[] }
interface AuditRow { id: string; action: string; username: string | null; summary: string | null; createdAt: string }

const inp = 'border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm w-full'
const btn = 'p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30'

function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="ปิด"><X size={18}/></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

// ── Status sets ──────────────────────────────────────────────────────────────
function StatusSetsModal({ sets, onClose }: { sets: StatusSet[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<{ id?: string; name: string; statuses: Status[] } | null>(null)
  const inv = () => qc.invalidateQueries({ queryKey: ['check-config'] })
  const save = useMutation({
    mutationFn: (s: NonNullable<typeof editing>) => s.id ? api.put(`/checks/status-sets/${s.id}`, s) : api.post('/checks/status-sets', s),
    onSuccess: () => { inv(); setEditing(null) }, onError: e => alert(errMsg(e)),
  })
  const del = useMutation({ mutationFn: (id: string) => api.delete(`/checks/status-sets/${id}`), onSuccess: inv, onError: e => alert(errMsg(e)) })
  const setSt = (i: number, patch: Partial<Status>) => setEditing(e => e && { ...e, statuses: e.statuses.map((s, j) => j === i ? { ...s, ...patch } : s) })

  return (
    <Modal title="Status sets" onClose={onClose} wide>
      {!editing ? (
        <div className="space-y-2">
          {sets.map(s => (
            <div key={s.id} className="flex items-center gap-2 border border-gray-100 rounded-xl p-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{s.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">{s.statuses.map(st => <span key={st.key} className="text-[11px] px-1.5 py-0.5 rounded text-white" style={{ background: st.color }}>{st.kind === 'ok' ? '✓' : st.kind === 'issue' ? '▲' : '⊘'} {st.label}</span>)}</div>
              </div>
              <button className={btn} onClick={() => setEditing({ id: s.id, name: s.name, statuses: s.statuses })} title="แก้ไข"><Pencil size={14}/></button>
              <button className={btn} onClick={() => confirm(`ลบ status set "${s.name}" ?`) && del.mutate(s.id)} title="ลบ"><Trash2 size={14}/></button>
            </div>
          ))}
          <button onClick={() => setEditing({ name: '', statuses: [{ key: '', label: 'OK', color: '#16a34a', kind: 'ok' }, { key: '', label: 'NG', color: '#dc2626', kind: 'issue' }] })}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"><Plus size={14}/> สร้าง status set ใหม่</button>
        </div>
      ) : (
        <div className="space-y-3">
          <input className={inp} placeholder="ชื่อ status set (เช่น P / O)" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}/>
          <div className="space-y-2">
            {editing.statuses.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-24" placeholder="label" value={s.label} onChange={e => setSt(i, { label: e.target.value })}/>
                <input type="color" value={s.color} onChange={e => setSt(i, { color: e.target.value })} className="w-9 h-8 rounded border border-gray-200" aria-label="สี"/>
                <select value={s.kind} onChange={e => setSt(i, { kind: e.target.value as Kind })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                  {(Object.keys(KIND_LABEL) as Kind[]).map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
                <button className={btn} onClick={() => setEditing({ ...editing, statuses: editing.statuses.filter((_, j) => j !== i) })} aria-label="ลบสถานะ"><Trash2 size={14}/></button>
              </div>
            ))}
            <button onClick={() => setEditing({ ...editing, statuses: [...editing.statuses, { key: '', label: '', color: '#6b7280', kind: 'ok' }] })} className="text-sm text-blue-600 hover:underline">+ เพิ่มสถานะ</button>
          </div>
          <p className="text-xs text-gray-500">เปลี่ยน label/สี/ประเภทได้โดยประวัติไม่เสีย (ระบบอ้างอิงด้วยรหัสภายใน) · สถานะที่มีบันทึกใช้อยู่ลบไม่ได้</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm text-gray-600">ยกเลิก</button>
            <button onClick={() => save.mutate(editing)} disabled={save.isPending} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">บันทึก</button>
          </div>
        </div>
      )}
    </Modal>
  )
}

// Asked when switching status set while records use keys that the new set does not have.
function KeyMapDialog({ unmapped, target, onConfirm, onClose }: { unmapped: string[]; target: Status[]; onConfirm: (m: Record<string, string>) => void; onClose: () => void }) {
  const [map, setMap] = useState<Record<string, string>>({})
  return (
    <Modal title="แมปสถานะเดิมไปยัง status set ใหม่" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">ประวัติเดิมใช้สถานะที่ไม่มีใน status set ใหม่ เลือกว่าจะแปลงเป็นสถานะใด (ประวัติจะถูกแปลงตามนี้)</p>
      <div className="space-y-2">
        {unmapped.map(k => (
          <div key={k} className="flex items-center gap-2 text-sm"><span className="w-24 font-mono">{k}</span>→
            <select className={inp} value={map[k] ?? ''} onChange={e => setMap({ ...map, [k]: e.target.value })}>
              <option value="">เลือก…</option>{target.map(s => <option key={s.key} value={s.key}>{s.label} ({KIND_LABEL[s.kind]})</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-4"><button disabled={unmapped.some(k => !map[k])} onClick={() => onConfirm(map)} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-40">ยืนยัน</button></div>
    </Modal>
  )
}

// ── Item dialog (create / edit) ──────────────────────────────────────────────
function ItemDialog({ group, item, mode, sets, today, onClose }: {
  group: Group; item?: Item; mode: 'item' | 'host'; sets: StatusSet[]; today: string; onClose: () => void
}) {
  const qc = useQueryClient()
  const hosts = group.items.filter(i => i.isHost)
  const [f, setF] = useState({
    name: item?.name ?? '', parentId: item?.parentId ?? '', statusSetId: item?.statusSetId ?? '', checkable: item?.checkable ?? false,
    activeFrom: item ? item.activeFrom ?? '' : today, activeTo: item?.activeTo ?? '',
  })
  const [keyMap, setKeyMap] = useState<{ unmapped: string[]; target: Status[] } | null>(null)
  const isHost = item?.isHost ?? mode === 'host'
  const showStatusSet = !isHost || f.checkable
  const mut = useMutation({
    mutationFn: (map?: Record<string, string>) => {
      const body = { name: f.name, parentId: f.parentId || null, statusSetId: f.statusSetId || null, checkable: f.checkable, activeFrom: f.activeFrom || null, activeTo: f.activeTo || null, keyMap: map }
      return item ? api.put(`/checks/items/${item.id}`, body) : api.post('/checks/items', { ...body, groupId: group.id, isHost })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['check-config'] }); qc.invalidateQueries({ queryKey: ['check-grid'] }); onClose() },
    onError: (e: any) => {
      if (e.response?.data?.code === 'NEEDS_KEY_MAP') {
        const set = sets.find(s => s.id === (f.statusSetId || group.statusSetId))
        setKeyMap({ unmapped: e.response.data.unmappedKeys, target: set?.statuses ?? [] })
      } else alert(errMsg(e))
    },
  })
  return (
    <Modal title={item ? `แก้ไข ${isHost ? 'Host' : 'รายการ'}` : isHost ? 'เพิ่ม Host' : 'เพิ่มรายการ'} onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-xs text-gray-500">ชื่อ<input className={`${inp} mt-1`} value={f.name} autoFocus onChange={e => setF({ ...f, name: e.target.value })}/></label>
        {!isHost && (
          <label className="block text-xs text-gray-500">อยู่ใต้ Host
            <select className={`${inp} mt-1`} value={f.parentId} onChange={e => setF({ ...f, parentId: e.target.value })}>
              <option value="">— ไม่มี (ระดับบนสุด) —</option>{hosts.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </label>
        )}
        {isHost && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={f.checkable} onChange={e => setF({ ...f, checkable: e.target.checked })}/>
              เปิดให้บันทึกผลตรวจ Host นี้ด้วย (นอกเหนือจากรายการย่อยข้างใต้)
            </label>
            {f.checkable && <p className="text-xs text-gray-500">ถ้าเพิ่งเปิดใช้งาน แนะนำปรับ "เริ่มใช้งาน" ด้านล่างเป็นวันนี้ ไม่เช่นนั้นช่วงก่อนหน้าจะนับเป็น "ยังไม่ได้ตรวจ" ย้อนหลังทั้งหมด</p>}
          </div>
        )}
        {showStatusSet && (
          <label className="block text-xs text-gray-500">Status set เฉพาะ{isHost ? 'Host' : 'รายการ'}นี้
            <select className={`${inp} mt-1`} value={f.statusSetId} onChange={e => setF({ ...f, statusSetId: e.target.value })}>
              <option value="">ใช้ตาม Group</option>{sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-500">เริ่มใช้งาน<input type="date" className={`${inp} mt-1`} value={f.activeFrom} onChange={e => setF({ ...f, activeFrom: e.target.value })}/></label>
          <label className="block text-xs text-gray-500">สิ้นสุดใช้งาน<input type="date" className={`${inp} mt-1`} value={f.activeTo} onChange={e => setF({ ...f, activeTo: e.target.value })}/></label>
        </div>
        <p className="text-xs text-gray-500">ช่องก่อนวันเริ่ม / หลังวันสิ้นสุด จะเป็น "ไม่เกี่ยวข้อง" (สีเทา) และไม่นับว่าตรวจไม่ครบ · เว้นวันเริ่มว่าง = นับตั้งแต่ต้น (จะทำให้ช่องในอดีตที่ไม่มีข้อมูลนับเป็น "ยังไม่ได้ตรวจ")</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">ยกเลิก</button>
          <button onClick={() => mut.mutate(undefined)} disabled={!f.name.trim() || mut.isPending} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">บันทึก</button>
        </div>
      </div>
      {keyMap && <KeyMapDialog {...keyMap} onClose={() => setKeyMap(null)} onConfirm={m => { setKeyMap(null); mut.mutate(m) }}/>}
    </Modal>
  )
}

// ── Import / copy structure / audit ──────────────────────────────────────────
function ImportModal({ group, onClose }: { group: Group; onClose: () => void }) {
  const qc = useQueryClient()
  const file = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [result, setResult] = useState('')
  const run = useMutation({
    mutationFn: () => {
      const f = file.current?.files?.[0]
      if (f) { const fd = new FormData(); fd.append('file', f); return api.post(`/checks/groups/${group.id}/import`, fd) }
      // pasted lines: "item" or "host<TAB or comma>item"
      const rows = text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
        const p = l.split(/\t|,/).map(x => x.trim()); return p.length > 1 ? { host: p[0], name: p[1] } : { name: p[0] }
      })
      return api.post(`/checks/groups/${group.id}/import`, { rows })
    },
    onSuccess: r => { qc.invalidateQueries({ queryKey: ['check-config'] }); setResult(`เพิ่ม ${r.data.created} รายการ · Host ใหม่ ${r.data.hostsCreated} · ข้ามที่ซ้ำ ${r.data.skipped}`) },
    onError: e => setResult(errMsg(e)),
  })
  return (
    <Modal title={`Import รายการเข้า "${group.name}"`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-gray-500">ไฟล์ Excel/CSV แถวแรกเป็นหัวคอลัมน์: <b>รายการ</b> (หรือ Item/Name/VM), <b>Host</b> (ไม่บังคับ), <b>วันเริ่ม</b> (YYYY-MM-DD ไม่บังคับ) · ชื่อซ้ำจะถูกข้าม</p>
        <input ref={file} type="file" accept=".csv,.xlsx,.xls" className="text-xs"/>
        <p className="text-xs text-gray-400 text-center">— หรือวางข้อความ (บรรทัดละรายการ, "Host,รายการ" เพื่อระบุ Host) —</p>
        <textarea rows={5} className={inp} value={text} onChange={e => setText(e.target.value)} placeholder={'Host1,VM-Web\nHost1,VM-DB\nCCTV Lobby'}/>
        {result && <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2">{result}</p>}
        <div className="flex justify-end"><button onClick={() => run.mutate()} disabled={run.isPending} className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg disabled:opacity-50"><Upload size={14}/> นำเข้า</button></div>
      </div>
    </Modal>
  )
}

function CopyStructureModal({ group, groups, today, onClose }: { group: Group; groups: Group[]; today: string; onClose: () => void }) {
  const qc = useQueryClient()
  const prev = new Date(`${today}T00:00:00Z`); prev.setUTCMonth(prev.getUTCMonth() - 1)
  const [src, setSrc] = useState(group.id)
  const [month, setMonth] = useState(prev.toISOString().slice(0, 7))
  const run = useMutation({
    mutationFn: () => api.post(`/checks/groups/${group.id}/copy-structure`, { sourceGroupId: src, month }),
    onSuccess: r => { qc.invalidateQueries({ queryKey: ['check-config'] }); alert(`คัดลอก ${r.data.copied} รายการ (ข้ามที่ซ้ำ ${r.data.skipped})`); onClose() },
    onError: e => alert(errMsg(e)),
  })
  return (
    <Modal title="คัดลอกโครงสร้างจากเดือนก่อน" onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-xs text-gray-500">คัดลอกจาก Group<select className={`${inp} mt-1`} value={src} onChange={e => setSrc(e.target.value)}>{groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select></label>
        <label className="block text-xs text-gray-500">รายการที่ใช้งานอยู่ในเดือน<input type="month" className={`${inp} mt-1`} value={month} onChange={e => setMonth(e.target.value)}/></label>
        <p className="text-xs text-gray-500">คัดลอกเฉพาะโครงสร้าง (Host/รายการ) เข้า "{group.name}" โดยเริ่มใช้งานตั้งแต่ต้นเดือนนี้ · ไม่คัดลอกผลตรวจ · ชื่อที่มีอยู่แล้วจะถูกข้าม</p>
        <div className="flex justify-end"><button onClick={() => run.mutate()} disabled={run.isPending} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">คัดลอก</button></div>
      </div>
    </Modal>
  )
}

function AuditModal({ onClose }: { onClose: () => void }) {
  const { data = [], isLoading } = useQuery<AuditRow[]>({ queryKey: ['check-audit'], queryFn: () => api.get('/checks/audit', { params: { type: 'config', limit: 100 } }).then(r => r.data) })
  return (
    <Modal title="ประวัติการแก้ไข config" onClose={onClose} wide>
      {isLoading ? <p className="text-sm text-gray-400">กำลังโหลด…</p> : data.length === 0 ? <p className="text-sm text-gray-400">ยังไม่มีประวัติ</p> : (
        <ul className="space-y-1.5">
          {data.map(a => (
            <li key={a.id} className="text-xs border-l-2 border-gray-200 pl-2 text-gray-700">
              <span className="text-gray-400">{new Date(a.createdAt).toLocaleString('th-TH')}</span> · <b>{a.username}</b> · <span className="font-mono text-[11px] bg-gray-100 px-1 rounded">{a.action}</span><br/>{a.summary}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

// ── Main ─────────────────────────────────────────────────────────────────────
type Dlg = { t: 'item'; mode: 'item' | 'host'; item?: Item } | { t: 'sets' } | { t: 'import' } | { t: 'copy' } | { t: 'audit' } | { t: 'newGroup' } | null

export default function ManageItems() {
  const qc = useQueryClient()
  const { data: cfg, isLoading } = useQuery<Config>({ queryKey: ['check-config'], queryFn: () => api.get('/checks/config').then(r => r.data) })
  const [gid, setGid] = useState('')
  const [dlg, setDlg] = useState<Dlg>(null)
  const [drag, setDrag] = useState<{ kind: 'item' | 'group'; id: string } | null>(null)
  const [keyMap, setKeyMap] = useState<{ unmapped: string[]; target: Status[]; retry: (m: Record<string, string>) => void } | null>(null)
  const inv = () => { qc.invalidateQueries({ queryKey: ['check-config'] }); qc.invalidateQueries({ queryKey: ['check-grid'] }); qc.invalidateQueries({ queryKey: ['check-dashboard'] }) }
  const fail = (e: any) => alert(errMsg(e))

  const seed = useMutation({ mutationFn: () => api.post('/checks/seed-defaults'), onSuccess: inv, onError: fail })
  const groupPut = useMutation({ mutationFn: (p: { id: string; body: any }) => api.put(`/checks/groups/${p.id}`, p.body), onSuccess: inv })
  const reorderGroups = useMutation({ mutationFn: (ids: string[]) => api.put('/checks/groups/reorder', { ids }), onSuccess: inv, onError: fail })
  const dupGroup = useMutation({ mutationFn: (id: string) => api.post(`/checks/groups/${id}/duplicate`), onSuccess: r => { inv(); setGid(r.data.id) }, onError: fail })
  const reorderItems = useMutation({ mutationFn: (p: { groupId: string; parentId: string | null; ids: string[] }) => api.put('/checks/items/reorder', p), onError: fail })

  if (isLoading || !cfg) return <p className="text-center text-gray-400 py-16">กำลังโหลด…</p>
  if (cfg.groups.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="text-gray-500">ยังไม่มี checklist</p>
        <button onClick={() => seed.mutate()} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm">สร้างชุดเริ่มต้น (Backup Daily / Check Server / Check System Others)</button>
        <div><button onClick={() => setDlg({ t: 'newGroup' })} className="text-sm text-blue-600 hover:underline">หรือสร้าง Group เอง</button></div>
        {dlg?.t === 'newGroup' && <GroupForm sets={cfg.statusSets} onClose={() => setDlg(null)} onCreated={id => { setGid(id); setDlg(null) }} openSets={() => setDlg({ t: 'sets' })}/>}
        {dlg?.t === 'sets' && <StatusSetsModal sets={cfg.statusSets} onClose={() => setDlg(null)}/>}
      </div>
    )
  }
  const group = cfg.groups.find(g => g.id === gid) ?? cfg.groups[0]
  const items = group.items
  const kids = (parentId: string | null) => items.filter(i => i.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder)
  const setName = (id: string) => cfg.statusSets.find(s => s.id === id)?.name ?? '?'

  // ── group settings ──
  const saveGroup = (body: any, retry?: Record<string, string>) => groupPut.mutate({ id: group.id, body: { ...body, keyMap: retry } }, {
    onError: (e: any) => {
      if (e.response?.data?.code === 'NEEDS_KEY_MAP') {
        const target = cfg.statusSets.find(s => s.id === body.statusSetId)?.statuses ?? []
        setKeyMap({ unmapped: e.response.data.unmappedKeys, target, retry: m => saveGroup(body, m) })
      } else fail(e)
    },
  })
  const changeFrequency = (f: Frequency) => {
    if (f === group.frequency) return
    if (confirm(`เปลี่ยนความถี่ "${group.name}" จาก ${FREQ_LABEL[group.frequency]} เป็น ${FREQ_LABEL[f]} ?\n\n• ประวัติเดิมยังอยู่ แต่ผลที่ไม่ตรงกับรอบใหม่ (เช่น วันที่ไม่ใช่วันจันทร์/วันที่ 1) จะไม่แสดงและไม่ถูกนับ\n• % ครบถ้วนย้อนหลังจะถูกคำนวณใหม่ตามความถี่ใหม่ทั้งหมด\n• Dashboard เดือนก่อนหน้าอาจเปลี่ยนไป\n\nแนะนำ: ถ้าต้องการเก็บรูปแบบเดิม ให้สร้าง Group ใหม่แทน`)) saveGroup({ frequency: f })
  }
  const deleteGroup = async () => {
    if (!confirm(`ลบ Group "${group.name}" ?`)) return
    try { await api.delete(`/checks/groups/${group.id}`); setGid(''); inv() }
    catch (e: any) {
      if (e.response?.data?.code === 'HAS_RECORDS') { if (confirm(`${e.response.data.message}\n\nยกเลิกใช้งาน Group นี้เลยหรือไม่? (ประวัติยังอยู่)`)) saveGroup({ isActive: false }) }
      else fail(e)
    }
  }

  // ── item actions ──
  const deleteItem = async (it: Item) => {
    if (!confirm(`ลบ "${it.name}" ถาวร?`)) return
    try { await api.delete(`/checks/items/${it.id}`); inv() }
    catch (e: any) {
      if (e.response?.data?.code === 'HAS_RECORDS') { if (confirm(`${e.response.data.message}\n\nยกเลิกใช้งานแทนหรือไม่?`)) deactivate(it, true) }
      else fail(e)
    }
  }
  const deactivate = async (it: Item, skipConfirm = false) => {
    if (!skipConfirm && !confirm(`ยกเลิกใช้งาน "${it.name}" ?\n\n• ประวัติเดิมยังอยู่ครบ\n• ช่องหลังจากนี้จะเป็น "ไม่เกี่ยวข้อง" และไม่นับว่าตรวจไม่ครบ\n• เปิดใช้งานใหม่ได้ภายหลัง`)) return
    try { await api.post(`/checks/items/${it.id}/deactivate`); inv() } catch (e) { fail(e) }
  }
  const reactivate = async (it: Item) => { try { await api.post(`/checks/items/${it.id}/reactivate`); inv() } catch (e) { fail(e) } }
  const dupItem = async (it: Item) => { try { await api.post(`/checks/items/${it.id}/duplicate`); inv() } catch (e) { fail(e) } }

  // ── ordering / moving between hosts ──
  const place = async (dragId: string, parentId: string | null, index: number | 'end') => {
    const it = items.find(i => i.id === dragId); if (!it || (it.isHost && parentId)) return
    const oldParent = it.parentId
    const target = kids(parentId).map(i => i.id).filter(id => id !== dragId)
    target.splice(index === 'end' ? target.length : index, 0, dragId)
    await reorderItems.mutateAsync({ groupId: group.id, parentId, ids: target })
    if (oldParent !== parentId) {
      const rest = kids(oldParent).map(i => i.id).filter(id => id !== dragId)
      if (rest.length) await reorderItems.mutateAsync({ groupId: group.id, parentId: oldParent, ids: rest })
    }
    inv()
  }
  const nudge = (it: Item, dir: -1 | 1) => {
    const ids = kids(it.parentId).map(i => i.id); const i = ids.indexOf(it.id); const j = i + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    reorderItems.mutate({ groupId: group.id, parentId: it.parentId, ids }, { onSuccess: inv })
  }
  const dropOnItem = (e: React.DragEvent, target: Item) => {
    e.preventDefault(); e.stopPropagation()
    if (!drag || drag.kind !== 'item' || drag.id === target.id) return
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const lower = (e.clientY - r.top) / r.height > 0.5
    const dragged = items.find(i => i.id === drag.id)
    if (target.isHost && lower && !dragged?.isHost) return place(drag.id, target.id, 'end')       // into the host
    const siblings = kids(target.parentId).map(i => i.id).filter(id => id !== drag.id)
    place(drag.id, target.parentId, Math.max(0, siblings.indexOf(target.id)))                      // before target
  }
  const dropOnGroup = (e: React.DragEvent, target: Group) => {
    e.preventDefault()
    if (!drag || drag.kind !== 'group' || drag.id === target.id) return
    const ids = cfg.groups.map(g => g.id).filter(id => id !== drag.id)
    ids.splice(ids.indexOf(target.id), 0, drag.id)
    reorderGroups.mutate(ids)
  }

  const renderItem = (it: Item, depth: number) => {
    const notStarted = it.activeFrom && it.activeFrom > cfg.today
    const ended = it.activeTo && it.activeTo <= cfg.today
    return (
      <div key={it.id} draggable onDragStart={e => { e.stopPropagation(); setDrag({ kind: 'item', id: it.id }) }} onDragEnd={() => setDrag(null)}
        onDragOver={e => { if (drag?.kind === 'item') e.preventDefault() }} onDrop={e => dropOnItem(e, it)}
        className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${it.isHost ? 'bg-slate-50 border-slate-200' : 'bg-white border-gray-100'} ${drag?.id === it.id ? 'opacity-40' : ''}`} style={{ marginLeft: depth * 24 }}>
        <GripVertical size={14} className="text-gray-300 cursor-grab flex-shrink-0" aria-hidden/>
        <div className="flex-1 min-w-0">
          <span className={`text-sm ${ended ? 'text-gray-400 line-through' : 'text-gray-900'} ${it.isHost ? 'font-semibold' : ''}`}>{it.isHost ? '▸ ' : ''}{it.name}</span>
          {it.isHost && it.checkable && <span className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 rounded">ตรวจสอบได้</span>}
          {it.statusSetId && <span className="ml-2 text-[10px] bg-purple-50 text-purple-700 border border-purple-100 px-1.5 rounded">{setName(it.statusSetId)}</span>}
          {(it.activeFrom || it.activeTo) && <span className="ml-2 text-[10px] text-gray-400">{it.activeFrom ? `เริ่ม ${it.activeFrom}` : ''}{it.activeTo ? ` · ถึง ${it.activeTo}` : ''}</span>}
          {notStarted && <span className="ml-1 text-[10px] text-amber-600">(ยังไม่เริ่ม)</span>}
          {ended && <span className="ml-1 text-[10px] text-gray-500">(ยกเลิกใช้งาน)</span>}
          {it.recordCount > 0 && <span className="ml-2 text-[10px] text-gray-300">{it.recordCount} records</span>}
        </div>
        <button className={btn} onClick={() => nudge(it, -1)} title="เลื่อนขึ้น" aria-label="เลื่อนขึ้น"><ArrowUp size={13}/></button>
        <button className={btn} onClick={() => nudge(it, 1)} title="เลื่อนลง" aria-label="เลื่อนลง"><ArrowDown size={13}/></button>
        <button className={btn} onClick={() => setDlg({ t: 'item', mode: it.isHost ? 'host' : 'item', item: it })} title="แก้ไข"><Pencil size={13}/></button>
        <button className={btn} onClick={() => dupItem(it)} title="ทำซ้ำ"><Copy size={13}/></button>
        {ended ? <button className={btn} onClick={() => reactivate(it)} title="เปิดใช้งานอีกครั้ง"><RotateCcw size={13}/></button>
               : <button className={btn} onClick={() => deactivate(it)} title="ยกเลิกใช้งาน (เก็บประวัติ)"><Ban size={13}/></button>}
        <button className={btn} onClick={() => deleteItem(it)} title="ลบ"><Trash2 size={13}/></button>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      {/* Groups */}
      <div className="space-y-2">
        <div className="flex gap-1.5">
          <button onClick={() => setDlg({ t: 'newGroup' })} className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-sm"><Plus size={14}/> Group ใหม่</button>
          <button onClick={() => setDlg({ t: 'sets' })} className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" title="จัดการ status set"><Palette size={15}/></button>
          <button onClick={() => setDlg({ t: 'audit' })} className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" title="ประวัติการแก้ไข"><History size={15}/></button>
        </div>
        {cfg.groups.map(g => (
          <div key={g.id} draggable onDragStart={() => setDrag({ kind: 'group', id: g.id })} onDragEnd={() => setDrag(null)}
            onDragOver={e => { if (drag?.kind === 'group') e.preventDefault() }} onDrop={e => dropOnGroup(e, g)}
            onClick={() => setGid(g.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer ${g.id === group.id ? 'border-blue-300 bg-blue-50' : 'border-gray-100 bg-white hover:bg-gray-50'} ${drag?.id === g.id ? 'opacity-40' : ''}`}>
            <GripVertical size={14} className="text-gray-300 cursor-grab" aria-hidden/>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium truncate ${g.isActive ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{g.name}</p>
              <p className="text-[11px] text-gray-500">{FREQ_LABEL[g.frequency]} · {g.items.filter(i => !i.isHost).length} รายการ{!g.isActive && ' · ปิดใช้งาน'}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Group detail */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4 min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <label className="text-xs text-gray-500">ชื่อ Group
            <input key={group.id + group.name} defaultValue={group.name} className={`${inp} mt-1`} onBlur={e => e.target.value.trim() && e.target.value !== group.name && saveGroup({ name: e.target.value })}/>
          </label>
          <label className="text-xs text-gray-500">ความถี่
            <select className={`${inp} mt-1`} value={group.frequency} onChange={e => changeFrequency(e.target.value as Frequency)}>
              {(Object.keys(FREQ_LABEL) as Frequency[]).map(f => <option key={f} value={f}>{FREQ_LABEL[f]}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500">ประเภท (ใช้คำนวณ Dashboard)
            <select className={`${inp} mt-1`} value={group.kind} onChange={e => saveGroup({ kind: e.target.value })}>
              {Object.entries(GROUP_KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500">Status set
            <select className={`${inp} mt-1`} value={group.statusSetId} onChange={e => saveGroup({ statusSetId: e.target.value })}>
              {cfg.statusSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <button onClick={() => setDlg({ t: 'item', mode: 'item' })} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg"><Plus size={14}/> รายการ</button>
          <button onClick={() => setDlg({ t: 'item', mode: 'host' })} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"><Plus size={14}/> Host</button>
          <button onClick={() => setDlg({ t: 'import' })} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"><Upload size={14}/> Import Excel/CSV</button>
          <button onClick={() => setDlg({ t: 'copy' })} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"><FolderInput size={14}/> คัดลอกโครงสร้างจากเดือนก่อน</button>
          <button onClick={() => dupGroup.mutate(group.id)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"><Copy size={14}/> ทำซ้ำ Group</button>
          {group.isActive
            ? <button onClick={() => confirm(`ยกเลิกใช้งาน Group "${group.name}"? (ประวัติยังอยู่ และไม่นับใน Dashboard)`) && saveGroup({ isActive: false })} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"><Ban size={14}/> ยกเลิกใช้งาน</button>
            : <button onClick={() => saveGroup({ isActive: true })} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"><RotateCcw size={14}/> เปิดใช้งาน</button>}
          <button onClick={deleteGroup} className="flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"><Trash2 size={14}/> ลบ Group</button>
        </div>

        <p className="text-xs text-gray-400">ลากเพื่อเรียงลำดับ · ลากไปวางบนครึ่งล่างของ Host เพื่อย้ายเข้าใน Host นั้น · (มือถือ/คีย์บอร์ดใช้ปุ่มลูกศร) · ลบจริงได้เฉพาะรายการที่ยังไม่มีบันทึก ถ้ามีแล้วใช้ "ยกเลิกใช้งาน"</p>

        <div className="space-y-1.5" onDragOver={e => drag?.kind === 'item' && e.preventDefault()} onDrop={e => { e.preventDefault(); if (drag?.kind === 'item') place(drag.id, null, 'end') }}>
          {items.length === 0 && <p className="text-sm text-gray-400 text-center py-8">ยังไม่มีรายการ</p>}
          {kids(null).map(top => (
            <div key={top.id} className="space-y-1.5">
              {renderItem(top, 0)}
              {top.isHost && kids(top.id).map(c => renderItem(c, 1))}
            </div>
          ))}
          {drag?.kind === 'item' && <div className="border-2 border-dashed border-gray-200 rounded-lg text-center text-xs text-gray-400 py-2">วางที่นี่ = ย้ายไปท้ายสุดของระดับบนสุด</div>}
        </div>
      </div>

      {dlg?.t === 'item' && <ItemDialog group={group} item={dlg.item} mode={dlg.mode} sets={cfg.statusSets} today={cfg.today} onClose={() => setDlg(null)}/>}
      {dlg?.t === 'sets' && <StatusSetsModal sets={cfg.statusSets} onClose={() => setDlg(null)}/>}
      {dlg?.t === 'import' && <ImportModal group={group} onClose={() => setDlg(null)}/>}
      {dlg?.t === 'copy' && <CopyStructureModal group={group} groups={cfg.groups} today={cfg.today} onClose={() => setDlg(null)}/>}
      {dlg?.t === 'audit' && <AuditModal onClose={() => setDlg(null)}/>}
      {dlg?.t === 'newGroup' && <GroupForm sets={cfg.statusSets} onClose={() => setDlg(null)} onCreated={id => { setGid(id); setDlg(null) }} openSets={() => setDlg({ t: 'sets' })}/>}
      {keyMap && <KeyMapDialog unmapped={keyMap.unmapped} target={keyMap.target} onClose={() => setKeyMap(null)} onConfirm={m => { const r = keyMap.retry; setKeyMap(null); r(m) }}/>}
    </div>
  )
}

function GroupForm({ sets, onClose, onCreated, openSets }: { sets: StatusSet[]; onClose: () => void; onCreated: (id: string) => void; openSets: () => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ name: '', frequency: 'DAILY' as Frequency, kind: 'GENERAL', statusSetId: sets[0]?.id ?? '' })
  const mk = useMutation({
    mutationFn: () => api.post('/checks/groups', f),
    onSuccess: r => { qc.invalidateQueries({ queryKey: ['check-config'] }); onCreated(r.data.id) }, onError: e => alert(errMsg(e)),
  })
  return (
    <Modal title="สร้าง Group ใหม่ (Checklist)" onClose={onClose}>
      <div className="space-y-3 text-left">
        <label className="block text-xs text-gray-500">ชื่อ (เช่น Check CCTV)<input className={`${inp} mt-1`} autoFocus value={f.name} onChange={e => setF({ ...f, name: e.target.value })}/></label>
        <label className="block text-xs text-gray-500">ความถี่<select className={`${inp} mt-1`} value={f.frequency} onChange={e => setF({ ...f, frequency: e.target.value as Frequency })}>{(Object.keys(FREQ_LABEL) as Frequency[]).map(k => <option key={k} value={k}>{FREQ_LABEL[k]}</option>)}</select></label>
        <label className="block text-xs text-gray-500">ประเภท<select className={`${inp} mt-1`} value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })}>{Object.entries(GROUP_KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label className="block text-xs text-gray-500">Status set
          <select className={`${inp} mt-1`} value={f.statusSetId} onChange={e => setF({ ...f, statusSetId: e.target.value })}>{sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        </label>
        {sets.length === 0 && <button onClick={openSets} className="text-xs text-blue-600 hover:underline">ยังไม่มี status set — สร้างก่อน</button>}
        <div className="flex justify-end"><button onClick={() => mk.mutate()} disabled={!f.name.trim() || !f.statusSetId || mk.isPending} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">สร้าง</button></div>
      </div>
    </Modal>
  )
}
